import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentUserResponse, DashboardLayout, ItemResponse, Permission } from '@proerp/shared';
import { apiRequest } from './apiClient';

const CURRENT_USER_KEY = ['auth', 'me'] as const;

/** Aktueller Benutzer inkl. effektiver Berechtigungen (/auth/me, ADR 0011). */
export function useCurrentUser() {
  return useQuery({
    queryKey: CURRENT_USER_KEY,
    queryFn: () => apiRequest<ItemResponse<CurrentUserResponse>>('/v1/auth/me'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Vom Nutzer selbst ausgeblendete Navigations-Gruppen + deren Reihenfolge
 * setzen (reine Anzeige-Präferenz, siehe `useNavItems`) — setzt die
 * `/auth/me`-Query-Cache direkt aus der Antwort, damit Sidebar/BottomNav
 * sofort reagieren, ohne auf einen Refetch zu warten.
 */
export function useUpdateNavPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { hiddenGroups: string[]; groupOrder: string[] }) =>
      apiRequest<ItemResponse<CurrentUserResponse>>('/v1/auth/me/nav-preferences', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(CURRENT_USER_KEY, response);
    },
  });
}

/**
 * Pro-Nutzer-Dashboard-Layout (Anordnung/Sichtbarkeit der Widgets) setzen — setzt
 * den `/auth/me`-Cache direkt aus der Antwort, damit das Dashboard sofort reagiert.
 */
export function useUpdateDashboardLayout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (layout: DashboardLayout) =>
      apiRequest<ItemResponse<CurrentUserResponse>>('/v1/auth/me/dashboard-layout', {
        method: 'PATCH',
        body: JSON.stringify(layout),
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(CURRENT_USER_KEY, response);
    },
  });
}

/** Prüft eine Berechtigung gegen die effektiven Rechte (`*` = alle). */
export function hasPermission(
  permissions: string[] | undefined,
  required: Permission,
): boolean {
  if (!permissions) return false;
  return permissions.includes('*') || permissions.includes(required);
}

/** Bequemer Hook: liefert eine Prüf-Funktion für Berechtigungen. */
export function usePermissions(): {
  can: (permission: Permission) => boolean;
  permissions: string[];
  isLoading: boolean;
} {
  const { data, isLoading } = useCurrentUser();
  const permissions = data?.data.permissions ?? [];
  return {
    can: (permission: Permission) => hasPermission(permissions, permission),
    permissions,
    isLoading,
  };
}
